import React from 'react';
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerHeader,
  List,
  ListItem,
  ListItemText,
  ListItemLink,
  Text,
  Divider,
} from '@razorpay/blade/components';

interface FindUanProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const FindPfUan = ({ isOpen, onDismiss }: FindUanProps): React.ReactElement => {
  return (
    <Drawer isOpen={isOpen} onDismiss={onDismiss} accessibilityLabel="How to find your UAN?">
      <DrawerHeader title="How to find your UAN?" />
      <DrawerBody>
        <Box width="100%" backgroundColor="surface.background.gray.intense" overflow="auto">
          <Box display="flex" flexDirection="column" gap="spacing.7">
            {/* Step 1: Visit the EPFO Website */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                color="surface.text.gray.normal"
                marginBottom="spacing.3">
                Step 1: Visit the EPFO Website
              </Text>
              <List variant="ordered" size="medium">
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Open your web browser.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Go to the official Employees' Provident Fund Organisation (EPFO) website:
                    <ListItemLink
                      href="https://epfindia.gov.in"
                      target="_blank"
                      rel="noopener noreferrer">
                      epfindia.gov.in
                    </ListItemLink>
                  </ListItemText>
                </ListItem>
              </List>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Step 2: Navigate to the UAN Link */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                color="surface.text.gray.normal"
                marginBottom="spacing.3">
                Step 2: Navigate to the UAN Link
              </Text>
              <List variant="ordered" size="medium">
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    On the homepage, look for the "Our Services" menu.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Click on "For Employees".
                  </ListItemText>
                </ListItem>
              </List>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Step 3: Find the UAN Information */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                color="surface.text.gray.normal"
                marginBottom="spacing.3">
                Step 3: Find the UAN Information
              </Text>
              <List variant="ordered" size="medium">
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Scroll down and find the section labeled "Member Services".
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Click on the "Know Your UAN Status" link.
                  </ListItemText>
                </ListItem>
              </List>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Step 4: Enter Required Details */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                color="surface.text.gray.normal"
                marginBottom="spacing.3">
                Step 4: Enter Required Details
              </Text>
              <List variant="ordered" size="medium">
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    You will be redirected to a new page.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Fill in the required information:
                  </ListItemText>
                  <List variant="unordered" size="small">
                    <ListItem>
                      <ListItemText color="surface.text.gray.muted">Name</ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText color="surface.text.gray.muted">Mobile Number</ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText color="surface.text.gray.muted">Email ID</ListItemText>
                    </ListItem>
                    <ListItem>
                      <ListItemText color="surface.text.gray.muted">
                        PF Number (if you have it)
                      </ListItemText>
                    </ListItem>
                  </List>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Check the box to confirm that you are not a robot, if prompted.
                  </ListItemText>
                </ListItem>
              </List>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Step 5: Get Your UAN */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                color="surface.text.gray.normal"
                marginBottom="spacing.3">
                Step 5: Get Your UAN
              </Text>
              <List variant="ordered" size="medium">
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Click the "Get Authorization Pin" button.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    You will receive an OTP (One-Time Password) on the registered mobile number.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Enter the OTP in the provided field and submit.
                  </ListItemText>
                </ListItem>
                <ListItem>
                  <ListItemText color="surface.text.gray.muted">
                    Your UAN number will be displayed on the screen.
                  </ListItemText>
                </ListItem>
              </List>
              <Divider marginTop="spacing.5" />
            </Box>

            {/* Alternative Method: Through Your Employer */}
            <Box>
              <Text
                size="medium"
                weight="semibold"
                color="surface.text.gray.normal"
                marginBottom="spacing.3">
                Alternative Method: Through Your Employer
              </Text>
              <Text color="surface.text.gray.muted">
                If you're unable to find your UAN online, you can also ask your employer's HR
                department for assistance. They should have your UAN number readily available.
              </Text>
            </Box>
          </Box>
        </Box>
      </DrawerBody>
    </Drawer>
  );
};

export default FindPfUan;
